import { Typography, Tabs } from '../../ui';
import { useSearchParams } from 'react-router-dom';
import { StockPage } from './StockPage';
import { StockHistoryPage } from './StockHistoryPage';
import { StockValuationPage } from './StockValuationPage';
import { ReturnedStockPage } from './ReturnedStockPage';
import { NonSellableStockPage } from './NonSellableStockPage';
import { usePermission } from '../../store/auth';

/**
 * Unified Stock page (tabs):
 *  - Stock — Live stock + Valuation merged: for STOCK.VALUATION.VIEW users the value KPIs + charts sit
 *    on top of the nested product → variant → batch table (which also carries value/margin columns and
 *    per-batch value); roles without that permission just see the quantity view. One source of truth.
 *  - Non-sellable — expired / QC-hold / blocked / past-expiry stock (warehouse-scoped). Shown only to
 *    users with a warehouse-view permission (WHM / Admin / SA), matching the endpoint gate.
 *  - Returned Stock — returned sub-lots and where they landed.
 *  - Stock History — the movement ledger.
 * The active tab is reflected in the URL (?tab=).
 */
export function InventoryStockPage() {
  const [sp, setSp] = useSearchParams();
  const canValuation = usePermission('STOCK.VALUATION.VIEW');
  // OR-semantics: WHM has WAREHOUSE.VIEW.ASSIGNED, Admin/SH have WAREHOUSE.VIEW, SA via '*'. Accounts (STOCK.VIEW
  // only) don't — so the tab hides for them exactly like the /stock/non-sellable endpoint refuses them.
  const canNonSellable = usePermission('WAREHOUSE.VIEW.ASSIGNED') || usePermission('WAREHOUSE.VIEW');
  const tab = sp.get('tab');
  const activeKey = tab === 'history' ? 'history'
    : tab === 'returned' ? 'returned'
    : (tab === 'non-sellable' && canNonSellable) ? 'non-sellable'
    : 'stock';

  return (
    <div>
      <Typography.Title level={4}>Stock &amp; Valuation</Typography.Title>
      <Tabs
        activeKey={activeKey}
        onChange={(k) => setSp({ tab: k }, { replace: true })}
        items={[
          {
            key: 'stock',
            label: 'Stock',
            children: (
              <>
                {canValuation && <StockValuationPage summaryOnly />}
                <StockPage embedded />
              </>
            ),
          },
          ...(canNonSellable ? [{ key: 'non-sellable', label: 'Non-sellable', children: <NonSellableStockPage embedded /> }] : []),
          { key: 'returned', label: 'Returned Stock', children: <ReturnedStockPage /> },
          { key: 'history', label: 'Stock History', children: <StockHistoryPage embedded /> },
        ]}
      />
    </div>
  );
}
